import { IGateway, IMetricasGateway, IMetricasGateways } from 'modelos/src';
import { TonoEstado } from '../../../../../componentes/detalle/detalle.components';

/** Un aviso sobre el estado del gateway (ratio, errores, ocupación). */
export interface AvisoGateway {
  tipo: 'advertencia' | 'alerta';
  mensaje: string;
}

/** Tráfico de los últimos 15 días, sumado de las métricas de ChirpStack. */
export interface TraficoGateway {
  uplinks: number;
  downlinks: number;
  /** Uplinks por downlink; Infinity si hay uplinks y ningún downlink. */
  ratio: number;
  erroresTx: number;
}

const decimal = (n: number): string =>
  n.toLocaleString('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export function textoRatio(t: TraficoGateway): string {
  if (t.ratio === Infinity) return '∞';
  return decimal(t.ratio);
}

export function trafico(metricas?: IMetricasGateways): TraficoGateway {
  const suma = (datos?: number[]) => (datos ?? []).reduce((s, v) => s + v, 0);
  const uplinks = suma(metricas?.rxPackets?.datasets?.[0]?.data);
  const downlinks = suma(metricas?.txPackets?.datasets?.[0]?.data);
  // Errores TX: los paquetes con estado distinto de OK
  const erroresTx = (metricas?.txPacketsPerStatus?.datasets ?? [])
    .filter((ds) => ds.label.toLowerCase() !== 'ok')
    .reduce((s, ds) => s + suma(ds.data), 0);
  const ratio =
    downlinks > 0 ? uplinks / downlinks : uplinks > 0 ? Infinity : 0;
  return { uplinks, downlinks, ratio, erroresTx };
}

/**
 * ToA promedio por canal de la última hora con datos, en segundos (el
 * airtime viene por hora y canal).
 */
export function toaUltimaHora(datos?: IMetricasGateway[]): number | null {
  if (!datos?.length) return null;
  const ultima = Math.max(...datos.map((d) => new Date(d.fecha).getTime()));
  const deEsaHora = datos.filter((d) => new Date(d.fecha).getTime() === ultima);
  const totalMs = deEsaHora.reduce((s, d) => s + (d.totalToA || 0), 0);
  return Math.round(totalMs / 1000 / (deEsaHora.length || 1));
}

/** Avisos con los mismos umbrales que tenía el detalle. */
export function avisos(t: TraficoGateway, toa: number | null): AvisoGateway[] {
  const lista: AvisoGateway[] = [];
  const ratio = textoRatio(t);
  // Ratio RX/TX bajo: mucho downlink relativo a uplink
  if (t.downlinks > 0 && t.ratio < 2) {
    lista.push({
      tipo: 'alerta',
      mensaje: `Ratio RX/TX crítico (${ratio}). El gateway está transmitiendo demasiado, reduciendo su capacidad de escucha. En LoRaWAN el ratio típico debería ser >10:1.`,
    });
  } else if (t.downlinks > 0 && t.ratio < 5) {
    lista.push({
      tipo: 'advertencia',
      mensaje: `Ratio RX/TX bajo (${ratio}). Alto tráfico de downlinks relativo a uplinks. Considerá reducir confirmaciones y downlinks.`,
    });
  }
  // Errores TX: más del 10 % de los downlinks
  if (t.downlinks > 0) {
    const tasa = (t.erroresTx / t.downlinks) * 100;
    if (tasa > 10) {
      lista.push({
        tipo: 'alerta',
        mensaje: `Tasa de errores TX alta (${decimal(tasa)} %). Más del 10 % de los downlinks fallan. Verificá la configuración del gateway.`,
      });
    }
  }
  // Ocupación de canal: >180 seg/hora (5 %) advertencia, >600 (17 %) alerta
  if (toa !== null && toa > 180) {
    const porcentaje = decimal((toa / 3600) * 100);
    lista.push(
      toa > 600
        ? {
            tipo: 'alerta',
            mensaje: `Ocupación de canal crítica (${toa} seg/hora, ${porcentaje} %). Alto riesgo de colisiones y pérdida de paquetes.`,
          }
        : {
            tipo: 'advertencia',
            mensaje: `Ocupación de canal moderada (${toa} seg/hora, ${porcentaje} %). Monitorear para evitar degradación.`,
          },
    );
  }
  return lista;
}

/**
 * Envío de stats de la última franja de 10 min, con el criterio del listado:
 * recibidos / esperados; óptimo desde 90 %, degradado desde 50 %.
 */
export function envioStats(g?: IGateway): {
  tono: TonoEstado;
  etiqueta: string;
  detalle?: string;
} {
  const rec = g?.statsRecibidos;
  const esp = g?.statsEsperados;
  if (rec == null || esp == null || esp <= 0) {
    return { tono: 'neutro', etiqueta: 'Sin información' };
  }
  const detalle = `${rec} de ${esp} en 10 min`;
  if (rec === 0) return { tono: 'apagado', etiqueta: 'Sin stats', detalle };
  const ratio = Math.min(1, rec / esp);
  const porcentaje = `${Math.round(ratio * 100)} %`;
  if (ratio >= 0.9) {
    return { tono: 'ok', etiqueta: `Óptimo (${porcentaje})`, detalle };
  }
  if (ratio >= 0.5) {
    return {
      tono: 'advertencia',
      etiqueta: `Degradado (${porcentaje})`,
      detalle,
    };
  }
  return { tono: 'alerta', etiqueta: `Crítico (${porcentaje})`, detalle };
}

/**
 * Ocupación de canal con el estado que calcula el cron (estadoSalud, por
 * ToA), el mismo que muestra el listado.
 */
export function ocupacion(g?: IGateway): {
  tono: TonoEstado;
  etiqueta: string;
} {
  switch (g?.estadoSalud) {
    case 'ok':
      return { tono: 'ok', etiqueta: 'Óptimo' };
    case 'warning':
      return { tono: 'advertencia', etiqueta: 'Moderado' };
    case 'error':
      return { tono: 'alerta', etiqueta: 'Alto' };
    default:
      return { tono: 'neutro', etiqueta: 'Sin información' };
  }
}
